import { Injectable, inject } from '@angular/core';
import {
  CULTURE_ROUTE_PARAM,
  CultureKeyType,
  FALLBACK_CULTURE,
} from '@constants/app.constants';
import { Store } from '@ngrx/store';
import { selectRouteParam } from '@selectors/router.selector';
import {
  addDateMonths,
  addDateYears,
  daysInMonthFromDate,
  indexOfFirstDayInMonthOfYear,
  isDateAfter,
  isDateBefore,
  isDateBetween,
  isDateSameDay,
  isDateSameMonth,
  substractDateMonths,
} from '@utils/date-utils';
import { DateTime } from 'luxon';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  combineLatestWith,
  map,
  take,
} from 'rxjs';

export type Day = {
  dateOfDay: DateTime;
  numberOfDay: number;
  isInRange: boolean;
  isFirstDayOfRange: boolean;
  isLastDayOfRange: boolean;
  isDisabled?: boolean;
};

export type Month = {
  id: string;
  dateOfMonth: DateTime;
  nameOfMonth: Date;
  days: Array<Day | null>;
};

@Injectable({
  providedIn: 'root',
})
export class DateRangePickerService {
  private store = inject(Store);

  private initialDate = DateTime.now();

  // stores the month that is currently displayed in the date range picker as the first month
  currentDisplayedMonth$ = new BehaviorSubject<DateTime>(this.initialDate);

  // stores the first date of the date range picker (for render first month in date range picker)
  startDate$ = new BehaviorSubject<DateTime>(this.initialDate);

  // stores the last date of the date range picker (for render last month in date range picker)
  endDate$ = new BehaviorSubject<DateTime>(addDateYears(this.initialDate, 1));

  // stores first selected date from the date range picker
  firstDateFromRange$ = new BehaviorSubject<DateTime | undefined>(undefined);

  // stores last selected date from the date range picker
  lastDateFromRange$ = new BehaviorSubject<DateTime | undefined>(undefined);

  // stores if the date range picker is open
  isOpen$ = new BehaviorSubject<boolean>(false);

  // stores if the date range picker is skipped
  isSkipped$ = new BehaviorSubject<boolean>(false);

  // stores if the date range picker is single date
  isSingleDate$ = new BehaviorSubject<boolean>(false);

  // stores all disabled dates in the date range picker
  disabledFirstDateRange$ = new BehaviorSubject<DateTime[]>([]);

  // stores all disabled dates in the date range picker (for second date)
  disabledSecondDateRange$ = new BehaviorSubject<DateTime[]>([]);

  // stores the active input in the date range picker
  activeInput$ = new BehaviorSubject<'first' | 'second' | null>(null);

  // stores the month id to scroll to in the date range picker
  scrollToMonthId$ = new BehaviorSubject<string | null>(null);

  // stores all months in the date range picker
  get months$() {
    return combineLatest([
      this.startDate$,
      this.endDate$,
      this.disabledFirstDateRange$,
      this.disabledSecondDateRange$,
      this.firstDateFromRange$,
      this.lastDateFromRange$,
      this.activeInput$,
    ]).pipe(
      map(
        ([
          startDate,
          endDate,
          disabledFirstDateRange,
          disabledSecondDateRange,
          firstDateFromRange,
          lastDateFromRange,
          activeInput,
        ]) => {
          let months: Month[] = [];
          let currentDate = DateTime.fromObject({
            year: startDate.get('year'),
            month: startDate.get('month'),
            day: 1,
          });

          while (currentDate <= endDate) {
            months = [
              ...months,
              {
                id: this.getIdByDate(currentDate),
                dateOfMonth: currentDate,
                nameOfMonth: this.getMonthAndYearFromDate(currentDate),
                days: this.generateMonthDays({
                  date: currentDate,
                  firstDateFromRange,
                  lastDateFromRange,
                  disabledFirstDateRange,
                  disabledSecondDateRange,
                  activeInput,
                }),
              },
            ];
            currentDate = addDateMonths(currentDate, 1);
          }

          return months;
        },
      ),
    );
  }

  get firstMonth$() {
    return this.currentDisplayedMonth$.pipe(
      combineLatestWith(this.months$),
      map(([currentDisplayedMonth, months]) => {
        return months.find((month) => {
          return isDateSameMonth(month.dateOfMonth, currentDisplayedMonth);
        });
      }),
    );
  }

  get secondMonth$() {
    return this.currentDisplayedMonth$.pipe(
      combineLatestWith(this.months$),
      map(([currentDisplayedMonth, months]) => {
        return months.find((month) => {
          return isDateSameMonth(
            month.dateOfMonth,
            addDateMonths(currentDisplayedMonth, 1),
          );
        });
      }),
    );
  }

  get isDisabledPrevMonth$() {
    return this.currentDisplayedMonth$.pipe(
      combineLatestWith(this.startDate$),
      map(([currentDisplayedMonth, startDate]) => {
        return isDateSameMonth(currentDisplayedMonth, startDate);
      }),
    );
  }

  get isDisabledNextMonth$() {
    return this.currentDisplayedMonth$.pipe(
      combineLatestWith(this.endDate$),
      map(([currentDisplayedMonth, endDate]) => {
        return isDateSameMonth(
          currentDisplayedMonth,
          substractDateMonths(endDate, 1),
        );
      }),
    );
  }

  get dayNamesOfWeek$() {
    return this.culture$.pipe(
      map((culture) => {
        return Array.from({ length: 7 }, (_, i) => i).map((_, i) => {
          const date = new Date(2021, 0, i + 4);
          return new Intl.DateTimeFormat(culture || FALLBACK_CULTURE, {
            weekday: 'short',
          }).format(date);
        });
      }),
    );
  }

  culture$ = this.store.select(
    selectRouteParam(CULTURE_ROUTE_PARAM),
  ) as Observable<CultureKeyType | undefined>;

  constructor() {
    this.updateCurrentDisplayedMonthOnStartDateChange();
  }

  onDayClick(day?: Day) {
    if (!day || day.isDisabled) {
      return;
    }

    this.isSkipped$.next(false);

    const firstDateFromRange = this.firstDateFromRange$.getValue();
    const lastDateFromRange = this.lastDateFromRange$.getValue();
    const isSingleDate = this.isSingleDate$.getValue();
    const activeInput = this.activeInput$.getValue();

    if (activeInput === 'first') {
      this.firstDateFromRange$.next(day.dateOfDay);

      if (lastDateFromRange) {
        if (isDateAfter(day.dateOfDay, lastDateFromRange)) {
          this.lastDateFromRange$.next(undefined);
        } else {
          this.isOpen$.next(false);
          return;
        }
      }

      if (isSingleDate) {
        this.isOpen$.next(false);
        return;
      }

      this.onChangeActiveInput('second');
      return;
    }

    if (activeInput === 'second') {
      this.lastDateFromRange$.next(day.dateOfDay);
      this.isOpen$.next(false);
      return;
    }

    // If there is no start date, or if there is a start date and an end date, or isSingleDate set the clicked day as the start date
    if (
      !firstDateFromRange ||
      (firstDateFromRange && lastDateFromRange) ||
      isSingleDate
    ) {
      this.firstDateFromRange$.next(day.dateOfDay);
      this.lastDateFromRange$.next(undefined);

      // If isSingleDate, close the date range picker
      if (isSingleDate) {
        this.isOpen$.next(false);
      }
      return;
    }

    // If there is a start date and the clicked day is before the start date, set the clicked day as the start date
    if (firstDateFromRange && isDateBefore(day.dateOfDay, firstDateFromRange)) {
      this.lastDateFromRange$.next(firstDateFromRange);
      this.firstDateFromRange$.next(day.dateOfDay);
      return;
    }

    // If there is a start date and no end date, set the clicked day as the end date
    if (firstDateFromRange && !lastDateFromRange) {
      this.lastDateFromRange$.next(day.dateOfDay);
      this.isOpen$.next(false);
      return;
    }
  }

  onNextMonthClick(): void {
    const currentDisplayedMonth = this.currentDisplayedMonth$.getValue();
    this.currentDisplayedMonth$.next(addDateMonths(currentDisplayedMonth, 1));
  }

  onPrevMonthClick(): void {
    const currentDisplayedMonth = this.currentDisplayedMonth$.getValue();
    this.currentDisplayedMonth$.next(addDateMonths(currentDisplayedMonth, -1));
  }

  onClickSkip(): void {
    this.firstDateFromRange$.next(undefined);
    this.lastDateFromRange$.next(undefined);
    this.isSkipped$.next(true);
    this.isOpen$.next(false);
  }

  onOpenModal(): void {
    this.updateCurrentDisplayedMonthOnOpenModal();
    this.isOpen$.next(true);
  }

  onCloseModal(): void {
    this.isOpen$.next(false);
  }

  onChangeActiveInput(activeInput: 'first' | 'second' | null): void {
    this.activeInput$.next(activeInput);
  }

  private updateCurrentDisplayedMonthOnOpenModal() {
    const currentDisplayedMonthValueToUpdate =
      this.firstDateFromRange$.getValue() ||
      this.startDate$.getValue() ||
      this.initialDate;
    this.currentDisplayedMonth$.next(currentDisplayedMonthValueToUpdate);
  }

  private getMonthAndYearFromDate(date: DateTime) {
    return date.toJSDate();
  }

  private getIdByDate(date: DateTime) {
    return `month-${date.toISODate({ format: 'basic' })?.slice(0, 6)}`;
  }

  private generateMonthDays({
    date,
    firstDateFromRange,
    lastDateFromRange,
    disabledFirstDateRange,
    disabledSecondDateRange,
    activeInput,
  }: {
    date: DateTime;
    firstDateFromRange?: DateTime;
    lastDateFromRange?: DateTime;
    disabledFirstDateRange: DateTime[];
    disabledSecondDateRange: DateTime[];
    activeInput: 'first' | 'second' | null;
  }) {
    const year = date.get('year');
    const month = date.get('month');
    const daysInMonth = daysInMonthFromDate(date);
    const indexOfFirstDay = indexOfFirstDayInMonthOfYear(year, month);

    let monthDays: Array<Day | null> = [];

    // Adds days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = DateTime.local(year, month, day);

      const isFirstDayOfRange =
        (firstDateFromRange &&
          isDateSameDay(currentDate, firstDateFromRange)) ||
        false;

      const isLastDayOfRange =
        (lastDateFromRange && isDateSameDay(currentDate, lastDateFromRange)) ||
        false;

      const isInRange =
        (firstDateFromRange &&
          lastDateFromRange &&
          isDateBetween(currentDate, firstDateFromRange, lastDateFromRange)) ||
        false;

      const isDisabled = this.isDisabledDate({
        currentDate,
        activeInput,
        disabledFirstDateRange,
        disabledSecondDateRange,
        firstDateFromRange,
        lastDateFromRange,
      });

      monthDays = [
        ...monthDays,
        {
          dateOfDay: currentDate,
          numberOfDay: day,
          isInRange,
          isFirstDayOfRange,
          isLastDayOfRange,
          isDisabled,
        },
      ];
    }

    // Adds null before the first day of the month
    for (let i = 0; i < indexOfFirstDay; i++) {
      monthDays = [null, ...monthDays];
    }

    return monthDays;
  }

  onResetState({
    all = true,
    resetStartDate = false,
    resetEndDate = false,
    resetFirstDateFromRange = false,
    resetLastDateFromRange = false,
    resetActiveInput = false,
    resetIsOpen = false,
    resetIsSingleDate = false,
    resetDisabledDates = false,
    resetdisabledSecondDateRange = false,
    resetSkipped = false,
  }: {
    all?: boolean;
    resetStartDate?: boolean;
    resetEndDate?: boolean;
    resetFirstDateFromRange?: boolean;
    resetLastDateFromRange?: boolean;
    resetActiveInput?: boolean;
    resetIsOpen?: boolean;
    resetIsSingleDate?: boolean;
    resetDisabledDates?: boolean;
    resetdisabledSecondDateRange?: boolean;
    resetSkipped?: boolean;
  }) {
    if (resetStartDate || all) {
      this.startDate$.next(this.initialDate);
    }
    if (resetEndDate || all) {
      this.endDate$.next(addDateYears(this.initialDate, 1));
    }
    if (resetFirstDateFromRange || all) {
      this.firstDateFromRange$.next(undefined);
    }
    if (resetLastDateFromRange || all) {
      this.lastDateFromRange$.next(undefined);
    }
    if (resetActiveInput || all) {
      this.activeInput$.next(null);
    }
    if (resetIsOpen || all) {
      this.isOpen$.next(false);
    }
    if (resetIsSingleDate || all) {
      this.isSingleDate$.next(false);
    }
    if (resetDisabledDates || all) {
      this.disabledFirstDateRange$.next([]);
    }
    if (resetdisabledSecondDateRange || all) {
      this.disabledSecondDateRange$.next([]);
    }
    if (resetSkipped || all) {
      this.isSkipped$.next(false);
    }
  }

  private updateCurrentDisplayedMonthOnStartDateChange() {
    this.startDate$.pipe(take(1)).subscribe((startDate) => {
      this.currentDisplayedMonth$.next(startDate || this.initialDate);
    });
  }

  private isDisabledDate = ({
    activeInput,
    currentDate,
    disabledFirstDateRange,
    disabledSecondDateRange,
    firstDateFromRange,
    lastDateFromRange,
  }: {
    activeInput: 'first' | 'second' | null;
    currentDate: DateTime;
    disabledFirstDateRange: DateTime[];
    disabledSecondDateRange: DateTime[];
    firstDateFromRange?: DateTime;
    lastDateFromRange?: DateTime;
  }) => {
    const startDate = this.startDate$.getValue();
    const endDate = this.endDate$.getValue();

    if (activeInput === 'first') {
      return (
        disabledFirstDateRange.some((disabledDate) =>
          isDateSameDay(disabledDate, currentDate),
        ) ||
        isDateBefore(currentDate, startDate) ||
        isDateAfter(currentDate, endDate)
      );
    }

    if (activeInput === 'second') {
      return (
        disabledSecondDateRange.some((disabledDate) =>
          isDateSameDay(disabledDate, currentDate),
        ) ||
        isDateBefore(currentDate, startDate) ||
        isDateAfter(currentDate, endDate) ||
        (firstDateFromRange
          ? isDateBefore(currentDate, firstDateFromRange)
          : false)
      );
    }

    // mobile version
    if (activeInput === null) {
      return (
        (!firstDateFromRange || (firstDateFromRange && lastDateFromRange)
          ? disabledFirstDateRange
          : disabledSecondDateRange
        ).some((disabledDate) => isDateSameDay(disabledDate, currentDate)) ||
        isDateBefore(currentDate, startDate) ||
        isDateAfter(currentDate, endDate)
      );
    }

    return false;
  };
}
